import {
  DefaultError,
  InfiniteData,
  keepPreviousData,
  QueryKey,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export type Role = Endpoints['GET /roles']['response']['roles'][number];

export const useRole = (roleId: string | undefined) => {
  const fetch = useFetch<Endpoints['GET /roles/:id']>();

  return useQuery({
    queryKey: ['roles', roleId],
    queryFn: () => fetch(`/roles/${roleId!}`),
    enabled: !!roleId,
  });
};

export const useRoles = (
  params: Endpoints['GET /roles']['query'] = {},
  options: {
    keepPreviousData?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /roles']>();

  return useQuery({
    queryKey: ['roles', params],
    queryFn: () => fetch('/roles', params),
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useInfiniteRoles = (params?: Endpoints['GET /roles']['query']) => {
  const fetch = useFetch<Endpoints['GET /roles']>();
  const limit = params?.limit ?? 20;

  return useInfiniteQuery<
    Endpoints['GET /roles']['response'],
    DefaultError,
    InfiniteData<Endpoints['GET /roles']['response']>,
    QueryKey,
    number
  >({
    queryFn: ({ pageParam }) => fetch('/roles', { ...params, offset: pageParam }),
    queryKey: ['roles', 'infinite', params],
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.roles.length < limit) {
        return undefined;
      }

      return lastPage.meta.offset + limit;
    },
  });
};

export const useCreateRole = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /roles']>();

  return useMutation({
    mutationFn: (params: Endpoints['POST /roles']['body']) =>
      fetch('/roles', undefined, {
        method: 'POST',
        body: params,
      }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useUpdateRole = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /roles/:id']>();

  return useMutation({
    mutationFn: ({
      roleId,
      ...body
    }: {
      roleId: string;
    } & Endpoints['PATCH /roles/:id']['body']) =>
      fetch(`/roles/${roleId}`, undefined, { method: 'PATCH', body }),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};

export const useDeleteRole = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /roles/:id']>();

  return useMutation({
    mutationFn: (roleId: string) =>
      fetch(`/roles/${roleId}`, undefined, {
        method: 'DELETE',
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
  });
};
